import { useMemo, useState } from "react"
import { Autocomplete, Box, Button, CircularProgress, Stack, TextField, Typography } from "@mui/material"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import Iconify from "minimals-template/components/Iconify"
import { getClientGroups } from "routes/schedule/my-schedules/my-custom-schedules/controller/get-client-trees"
import { evaluateRuleSetPreview } from "routes/rule-sets/controller/evaluate-rule-set-preview"
import { PreviewSummary } from "./preview-summary"

export function RuleSetPreviewPanel({ ruleSetId }) {
    const { rateCardId } = useBoundContext()
    const [selectedRegime, setSelectedRegime] = useState(null)
    const [preview, setPreview] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)

    const clientGroups = getClientGroups.useResults()
    const regimes = useMemo(buildRegimeOptions, [clientGroups])

    return (
        <Stack spacing={2}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, lineHeight: "28px" }}>Preview Rule Set</Typography>
            <Typography variant="body2" color="text.secondary">
                Select a regime to preview how this rule set would affect its tasks. Save the rule set first.
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center">
                <Autocomplete
                    options={regimes}
                    getOptionLabel={getOptionLabel}
                    value={selectedRegime}
                    onChange={handleRegimeChange}
                    renderInput={renderInput}
                    sx={{ minWidth: 300 }}
                    isOptionEqualToValue={isOptionEqual}
                />
                <Button
                    variant="text"
                    startIcon={
                        <If value={loading}>
                            <CircularProgress size={18} color="inherit" />
                            <Else>
                                <Iconify icon="eva:eye-fill" width={20} />
                            </Else>
                        </If>
                    }
                    onClick={handlePreview}
                    disabled={!selectedRegime || loading}
                >
                    Preview
                </Button>
            </Stack>
            <If value={error}>
                <Typography variant="body2" color="error">
                    {error}
                </Typography>
            </If>
            <If value={preview}>
                <Box sx={{ mt: 2 }}>
                    <PreviewSummary preview={preview} />
                </Box>
            </If>
        </Stack>
    )

    function buildRegimeOptions() {
        return (clientGroups ?? []).flatMap((g) => {
            const entries = []
            if (g.hasLive) {
                entries.push({ id: `live|${g._id}`, label: g.name })
            }
            if (g.hasReady && !g.hasLive) {
                entries.push({ id: `ready|${g._id}`, label: `${g.name} (Ready)` })
            }
            if (!g.hasLive && !g.hasReady) {
                entries.push({ id: g._id, label: g.name })
            }
            return entries
        })
    }

    function getOptionLabel(option) {
        return option?.label || ""
    }

    function isOptionEqual(option, value) {
        return option?.id === value?.id
    }

    function renderInput(params) {
        return <TextField {...params} placeholder="Select a regime" />
    }

    function handleRegimeChange(_, value) {
        setSelectedRegime(value)
        setPreview(null)
        setError(null)
    }

    async function handlePreview() {
        setError(null)
        setPreview(null)
        setLoading(true)
        try {
            const result = await evaluateRuleSetPreview({ ruleSetId, regimeId: selectedRegime.id, rateCardId })
            setPreview(result)
        } catch (e) {
            setError(e.message || "Failed to evaluate rule set preview")
        } finally {
            setLoading(false)
        }
    }
}
